import Component from '@glimmer/component';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import {
  HdsLinkStandalone,
  HdsStepperList,
  HdsCodeBlock,
} from '@hashicorp/design-system-components/components';
import { boxPadding } from 'core/modifiers';
import templateString from 'core/utils/template-string';
import { AZURE_PEERING_TERRAFORM_TAB_DOWNLOAD_TF_CLICKED } from 'core/utils/consts/analytics-events/platform';
import CompletionSteps from 'hcp/components/page/hvn-peerings/detail/content-tabs/azure/completion-steps';

let terraform_snippet = templateString`provider "azurerm" {
  features {}
}

locals {
  application_id = "${'application_id'}"
  role_def_name  = join("-", ["hcp-hvn-peering-access", local.application_id])
  vnet_id        = "/subscriptions/${'subscription_id'}/resourceGroups/${'resource_group'}/providers/Microsoft.Network/virtualNetworks/${'vnet_name'}"
}

resource "azuread_service_principal" "principal" {
  application_id = local.application_id
}

resource "azurerm_role_definition" "definition" {
  name  = local.role_def_name
  scope = local.vnet_id

  assignable_scopes = [
    local.vnet_id
  ]

  permissions {
    actions = [
      "Microsoft.Network/virtualNetworks/peer/action",
      "Microsoft.Network/virtualNetworks/virtualNetworkPeerings/read",
      "Microsoft.Network/virtualNetworks/virtualNetworkPeerings/write"
    ]
  }
}

resource "azurerm_role_assignment" "role_assignment" {
  principal_id       = azuread_service_principal.principal.id
  role_definition_id = azurerm_role_definition.definition.role_definition_resource_id
  scope              = local.vnet_id
}`;

/**
 *
 * `tab` will be the selected tab;
 * @argument tab;
 * @type {String}
 *
 */
/**
 *
 * `product` such as consul or vault;
 * @argument product;
 * @type {String}
 *
 */
/**
 *
 * `model` will be a model containing the peering detail;
 * @argument model;
 * @type {Object}
 *
 */
/**
 *
 * `HvnPeeringsDetailAzureTerraformCodeSnippetComponent` renders the HVN Peerings Detail tabs content
 *
 *
 * ```
 * <Page::HvnPeeringsDetail::ContentTabs::Azure::CodeSnippets::Terraform
 *   @model={{model}}
 *   @tab={{tab}}
 *   @product={{product}}
 * />
 * ```
 *
 * @class HvnPeeringsDetailAzureTerraformCodeSnippetComponent
 *
 */
export default class PageHvnPeeringsDetailTabsContentAzureTerraform extends Component {
  @service intl;
  @service analytics;
  get applicationId() {
    return this.args.model.peering.target.azureTarget.applicationId;
  }

  get vnetName() {
    return this.args.model.peering.target.azureTarget.vnetName;
  }

  get subscriptionId() {
    return this.args.model.peering.target.azureTarget.subscriptionId;
  }

  get resourceGroupName() {
    return this.args.model.peering.target.azureTarget.resourceGroupName;
  }

  get terraformCodeSnippet() {
    let string = terraform_snippet({
      application_id: this.applicationId,
      vnet_name: this.vnetName,
      subscription_id: this.subscriptionId,
      resource_group: this.resourceGroupName,
    });
    return string;
  }

  sendDownloadTfClickedEvent = () => {
    this.analytics.trackEvent(AZURE_PEERING_TERRAFORM_TAB_DOWNLOAD_TF_CLICKED);
  };

  <template>
    <div
      data-test-peerings-content-tabs-terraform-azure
      {{boxPadding "md 0 0 0"}}
    >
      <div {{boxPadding "md 0 md 0"}}>
        <HdsLinkStandalone
          @iconPosition="trailing"
          @icon="docs-link"
          @text={{t
            "components.page.hvn-peerings.detail.instructions.azure.pending-acceptance.terraform.link.text"
          }}
          @href={{t
            "components.page.hvn-peerings.detail.instructions.azure.pending-acceptance.terraform.link.href"
          }}
        />
      </div>

      <HdsStepperList
        @titleTag="h3"
        @ariaLabel="Azure Cloud Terraform instructions"
        as |S|
      >
        <S.Step>
          <:title>
            {{t
              "components.page.hvn-peerings.detail.instructions.azure.pending-acceptance.terraform.step-one.label"
            }}
          </:title>
          <:content>
            <div {{boxPadding "md 0 0 0"}}>
              <HdsLinkStandalone
                data-test-terraform-download-button
                @icon="download"
                @text={{t
                  "page.terraform-quickstart.tf-configuration.editor.action.text"
                }}
                @href={{t
                  "page.terraform-quickstart.tf-configuration.editor.action.href"
                }}
                {{on "click" this.sendDownloadTfClickedEvent}}
              />
              <HdsCodeBlock
                @value={{this.terraformCodeSnippet}}
                @hasCopyButton={{true}}
                @language="shell"
                as |CB|
              >
                <CB.Description>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.azure.pending-acceptance.terraform.step-one.message"
                  }}
                </CB.Description>
              </HdsCodeBlock>
            </div>
          </:content>
        </S.Step>
      </HdsStepperList>
    </div>
    <CompletionSteps @startListNumber={{1}} />
  </template>
}
